/**
 * PageBanners.jsx
 * Alle pagina-specifieke hero banners in rankingCoach-stijl.
 * Export: gebruik de juiste banner per pagina.
 */
import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Search, Target, Zap, Globe, Share2, TrendingUp,
  MapPin, Star, CheckCircle, Users, Award, BarChart2, Briefcase,
  BookOpen, Shield, Layers, Settings, Clock, Mail
} from 'lucide-react';

/* ── Helpers ──────────────────────────────────────────────────────────── */
const FadeIn = ({ children, delay = 0, x = 0, y = 20 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, x, y }}
      animate={inView ? { opacity: 1, x: 0, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

const GradientBg = ({ children, style = {} }) => (
  <section style={{
    background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 55%, #0f172a 100%)',
    paddingTop: '100px', paddingBottom: '64px', position: 'relative', overflow: 'hidden',
    ...style
  }}>
    <div style={{ position: 'absolute', top: '10%', right: '5%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,160,23,0.1), transparent)', filter: 'blur(80px)', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', bottom: '0', left: '5%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.08), transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />
    {children}
  </section>
);

const Badge = ({ children }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(212,160,23,0.15)', border: '1px solid rgba(212,160,23,0.25)', borderRadius: '100px', padding: '7px 16px', marginBottom: '20px' }}>
    <span style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, fontFamily: "'Sora', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>{children}</span>
  </div>
);

const H1 = ({ children }) => (
  <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 'clamp(30px, 4vw, 52px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15, color: 'white', marginBottom: '18px' }}>
    {children}
  </h1>
);

const Sub = ({ children }) => (
  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '17px', lineHeight: 1.7, marginBottom: '28px', maxWidth: '520px' }}>
    {children}
  </p>
);

const CTAs = ({ primary, primaryHref = '/scan', secondary, secondaryHref }) => (
  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
    {primary && <Link to={primaryHref} className="rc-btn rc-btn-primary">{primary} <ArrowRight size={16} /></Link>}
    {secondary && <Link to={secondaryHref || '/over-ons'} className="rc-btn rc-btn-dark">{secondary}</Link>}
  </div>
);

const RatingStrip = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', padding: '14px 0', background: 'white', borderBottom: '1px solid #f1f5f9', fontSize: '14px' }}>
    <span style={{ color: '#64748b', fontWeight: 500 }}>Onze klanten zeggen:</span>
    <span style={{ color: '#0f172a', fontWeight: 700 }}>Uitstekend</span>
    <span style={{ color: '#D4A017', fontSize: '18px', letterSpacing: '2px' }}>★★★★★</span>
    <span style={{ color: '#64748b' }}><strong>4,8</strong> uit 5 — <strong>238+</strong> beoordelingen</span>
  </div>
);

/* ══════════════════════════════════════════════════════════════════════
   BANNERS PER PAGINA
══════════════════════════════════════════════════════════════════════ */

/* ── Diensten banner ────────────────────────────────────────────────── */
export const DienstenBanner = () => {
  const modules = [
    { icon: Search, label: 'SEO', color: '#059669' },
    { icon: Target, label: 'Google Ads', color: '#D4A017' },
    { icon: Zap, label: 'Lead Generatie', color: '#8b5cf6' },
    { icon: Globe, label: 'Website', color: '#3b82f6' },
    { icon: Share2, label: 'Social Ads', color: '#0EA5E9' },
    { icon: TrendingUp, label: 'Strategie', color: '#f59e0b' },
  ];
  return (
    <>
      <RatingStrip />
      <GradientBg>
        <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'center' }}>
            <FadeIn>
              <Badge>Onze diensten</Badge>
              <H1>Alle online marketing diensten<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>in één platform</span></H1>
              <Sub>Van eenmalige optimalisaties tot complete maandelijkse begeleiding. Kies de dienst die bij uw groeifase past.</Sub>
              <CTAs primary="Start gratis scan" primaryHref="/register" secondary="Bekijk prijzen" secondaryHref="/prijs" />
            </FadeIn>
            <FadeIn delay={0.15} x={40}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {modules.map(({ icon: Icon, label, color }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.2s', cursor: 'default' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = `${color}40`; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                    <div style={{ width: '36px', height: '36px', background: `${color}15`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon style={{ width: '18px', height: '18px', color }} />
                    </div>
                    <span style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>{label}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </GradientBg>
    </>
  );
};

/* ── Portfolio banner ───────────────────────────────────────────────── */
export const PortfolioBanner = () => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'center' }}>
          <FadeIn>
            <Badge>Portfolio</Badge>
            <H1>238+ succesvolle<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>marketingcampagnes</span></H1>
            <Sub>Van SEO en Google Ads tot lead generatie — wij leveren meetbare resultaten voor bedrijven door heel Nederland.</Sub>
            <CTAs primary="Start gratis scan" primaryHref="/register" secondary="Over ons" secondaryHref="/over-ons" />
          </FadeIn>
          <FadeIn delay={0.15} x={40}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[['238+', 'Campagnes', '#D4A017'], ['€18M+', 'Omzet gegenereerd', '#22c55e'], ['95%', 'Klantretentie', '#3b82f6'], ['8+', 'Jaar expertise', '#8b5cf6']].map(([v, l, c]) => (
                <div key={l} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${c}25`, borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
                  <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '32px', fontWeight: 800, color: c, margin: 0, lineHeight: 1 }}>{v}</p>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '6px' }}>{l}</p>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </GradientBg>
  </>
);

/* ── Prijzen banner ─────────────────────────────────────────────────── */
export const PrijzenBanner = ({ billing, setBilling }) => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10, textAlign: 'center', maxWidth: '700px', margin: '0 auto' }}>
        <FadeIn>
          <Badge>Transparante prijzen</Badge>
          <H1>Eenvoudige, transparante<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>prijzen voor elk bedrijf</span></H1>
          <Sub style={{ margin: '0 auto 32px' }}>Start gratis en upgrade wanneer u klaar bent. Geen verplichtingen, geen verborgen kosten.</Sub>

          {/* Billing toggle */}
          <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '14px', padding: '5px', gap: '4px' }}>
            {[['monthly', 'Maandelijks'], ['yearly', 'Jaarlijks  — bespaar 40%']].map(([v, l]) => (
              <button key={v} onClick={() => setBilling(v)}
                style={{ padding: '10px 22px', borderRadius: '10px', fontWeight: 600, fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'all 0.25s', fontFamily: "'Inter', sans-serif",
                  background: billing === v ? 'white' : 'transparent',
                  color: billing === v ? '#0f172a' : 'rgba(255,255,255,0.6)',
                  boxShadow: billing === v ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                }}>
                {l}
              </button>
            ))}
          </div>
        </FadeIn>
      </div>
    </GradientBg>
  </>
);

/* ── Blog banner ────────────────────────────────────────────────────── */
export const BlogBanner = () => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'center' }}>
          <FadeIn>
            <Badge>Blog & Kennisbank</Badge>
            <H1>Gratis marketing kennis<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>die jou verder helpt</span></H1>
            <Sub>Praktische tips, bewezen strategieën en actuele inzichten uit de marketingwereld — elke week nieuwe artikelen.</Sub>
            <CTAs primary="Start gratis scan" primaryHref="/register" secondary="Alle artikelen" secondaryHref="#artikelen" />
          </FadeIn>
          <FadeIn delay={0.15} x={40}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                ['SEO', 'SEO in 2026: zo komt u bovenaan in Google', '#059669'],
                ['Google Ads', 'Meer leads genereren met Google Ads', '#D4A017'],
                ['Lead Generatie', 'Van 0 naar 100+ leads per maand', '#8b5cf6'],
              ].map(([cat, title, c]) => (
                <div key={title} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ background: `${c}20`, border: `1px solid ${c}30`, borderRadius: '8px', padding: '4px 10px', flexShrink: 0 }}>
                    <span style={{ color: c, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat}</span>
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', fontWeight: 500 }}>{title}</span>
                  <ArrowRight style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.3)', marginLeft: 'auto', flexShrink: 0 }} />
                </div>
              ))}
              <div style={{ textAlign: 'center', paddingTop: '4px' }}>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>En nog 100+ artikelen meer...</span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </GradientBg>
  </>
);

/* ── Werken Bij banner ──────────────────────────────────────────────── */
export const WerkenBijBanner = ({ count = 10 }) => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'center' }}>
          <FadeIn>
            <Badge>{count} openstaande vacatures</Badge>
            <H1>Bouw mee aan de groei van<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>honderden bedrijven</span></H1>
            <Sub>Werken bij IMPERIA AI betekent impact maken, doorgroeien en werken aan uitdagende projecten voor echte klanten.</Sub>
            <CTAs primary="Bekijk vacatures" primaryHref="#vacatures" secondary="Over ons" secondaryHref="/over-ons" />
          </FadeIn>
          <FadeIn delay={0.15} x={40}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                [Award, '€1.500 opleidingsbudget/jaar', '#D4A017'],
                [Users, 'Hecht en ambitieus team', '#22c55e'],
                [TrendingUp, 'Snelle doorgroeimogelijkheden', '#3b82f6'],
                [Clock, 'Flexibele werktijden', '#8b5cf6'],
                [Shield, 'Marktconform salaris + bonus', '#f59e0b'],
              ].map(([Icon, text, color]) => (
                <div key={text} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', background: `${color}15`, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon style={{ width: '16px', height: '16px', color }} />
                  </div>
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '13px', fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </GradientBg>
  </>
);

/* ── Offerte banner ─────────────────────────────────────────────────── */
export const OfferteBanner = () => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10, textAlign: 'center', maxWidth: '680px', margin: '0 auto' }}>
        <FadeIn>
          <Badge>Vrijblijvend</Badge>
          <H1>Vraag een vrijblijvende<br /><span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>offerte aan</span></H1>
          <Sub style={{ margin: '0 auto 24px' }}>Selecteer de diensten die u nodig heeft en ontvang binnen 24 uur een voorstel op maat van een persoonlijk adviseur.</Sub>
          <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[['✓', 'Binnen 24 uur reactie'], ['✓', 'Vrijblijvend voorstel'], ['✓', 'Persoonlijk advies']].map(([icon, text]) => (
              <span key={text} style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#D4A017', fontWeight: 700 }}>{icon}</span>{text}
              </span>
            ))}
          </div>
        </FadeIn>
      </div>
    </GradientBg>
  </>
);

/* ── Radar/Pricing banner ───────────────────────────────────────────── */
export const RadarBanner = ({ billing, setBilling }) => (
  <PrijzenBanner billing={billing} setBilling={setBilling} />
);

/* ── Generic service page banner ───────────────────────────────────── */
export const ServiceBanner = ({ badge, title, titleHighlight, subtitle, primaryCta = 'Offerte aanvragen', primaryHref = '/offerte', icon: Icon, services = [] }) => (
  <>
    <RatingStrip />
    <GradientBg>
      <div className="container-custom" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '48px', alignItems: 'center' }}>
          <FadeIn>
            {badge && <Badge>{badge}</Badge>}
            <H1>{title}{' '}{titleHighlight && <span style={{ background: 'linear-gradient(135deg, #D4A017, #f5c842)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{titleHighlight}</span>}</H1>
            <Sub>{subtitle}</Sub>
            <CTAs primary={primaryCta} primaryHref={primaryHref} secondary="Bekijk prijzen" secondaryHref="/prijs" />
          </FadeIn>
          {services.length > 0 && (
            <FadeIn delay={0.15} x={40}>
              <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '24px' }}>
                <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' }}>{badge} diensten</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {services.map(s => (
                    <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '12px 16px' }}>
                      <span style={{ color: 'white', fontSize: '13px', fontWeight: 500 }}>{s.name}</span>
                      <span style={{ color: '#D4A017', fontWeight: 700, fontSize: '13px' }}>{s.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}
        </div>
      </div>
    </GradientBg>
  </>
);

export { RatingStrip };
